/**
 * Delivery deal pricing utilities.
 * Driver price bounds, margin calculation, and validation thresholds.
 *
 * Env (optional, VITE_ prefix):
 *   VITE_DRIVER_PRICE_MIN_PERCENT_THRESHOLD   - validation floor (default 0.2 = 20%)
 *   VITE_DRIVER_PRICE_MAX_PERCENT_THRESHOLD   - validation ceiling (default 0.8 = 80%); backend must match
 *   VITE_DRIVER_PRICE_DEFAULT_MIN_PERCENT     - default form min (default 0.3 = 30%)
 *   VITE_DRIVER_PRICE_DEFAULT_MAX_PERCENT     - default form max (default 0.7 = 70%)
 *   VITE_MIN_MARGIN_THRESHOLD                 - low-margin warning (default 0.55)
 *
 * Note: The backend has its own validation. If you change thresholds via env,
 * you may need to update the backend validation to stay in sync.
 */

import i18next from "i18next";

/**
 * Coerce unknown (e.g. useWatch) to number for pricing fields. Avoids `as number` casts.
 */
export function toPriceNumber(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// ---------------------------------------------------------------------------
// Validation thresholds — max allowed driver price as % of client price.
// User can adjust via env; backend must match or deals may be rejected.
// ---------------------------------------------------------------------------

/** Lowest allowed driver price as fraction of client price (e.g. 0.2 = 20%). */
export const DRIVER_PRICE_MIN_PERCENT_THRESHOLD =
  Number(import.meta.env.VITE_DRIVER_PRICE_MIN_PERCENT_THRESHOLD) || 0.2;

/** Highest allowed driver price as fraction of client price (e.g. 0.8 = 80%). Backend validates this too. */
export const DRIVER_PRICE_MAX_PERCENT_THRESHOLD =
  Number(import.meta.env.VITE_DRIVER_PRICE_MAX_PERCENT_THRESHOLD) || 0.8;

// ---------------------------------------------------------------------------
// Default UI values — initial min/max shown in the form, 10% inside thresholds.
// Users can override these; validation enforces the thresholds above.
// ---------------------------------------------------------------------------

/** Default min driver price (e.g. 0.4 = 40%, 10% above MIN threshold). */
export const DRIVER_PRICE_DEFAULT_MIN_PERCENT =
  Number(import.meta.env.VITE_DRIVER_PRICE_DEFAULT_MIN_PERCENT) ||
  DRIVER_PRICE_MIN_PERCENT_THRESHOLD + 0.1;

/** Default max driver price (e.g. 0.6 = 60%, 10% below MAX threshold). */
export const DRIVER_PRICE_DEFAULT_MAX_PERCENT =
  Number(import.meta.env.VITE_DRIVER_PRICE_DEFAULT_MAX_PERCENT) ||
  DRIVER_PRICE_MAX_PERCENT_THRESHOLD - 0.1;

/** Margin below which a low-margin warning is shown (e.g. 0.55 = 55%). */
export const MIN_MARGIN_THRESHOLD = Number(import.meta.env.VITE_MIN_MARGIN_THRESHOLD) || 0.55;

/** Recommended driver price as fraction of client price (e.g. 0.45 = 45%). */
export const DRIVER_RECOMMENDED_PRICE_PERCENT =
  Number(import.meta.env.VITE_DRIVER_RECOMMANDED_PRICE) || DRIVER_PRICE_DEFAULT_MAX_PERCENT;

export function calculateMargin(clientPrice: number, driverPrice: number): number {
  if (clientPrice === 0) return 0;
  return (clientPrice - driverPrice) / clientPrice;
}

export function roundCurrency(value: number): number {
  return Number(value.toFixed(2));
}

/** Floor to 2 decimals. Use for max so values never exceed validation threshold. */
function floorCurrency(value: number): number {
  return Math.floor(value * 100) / 100;
}

/** Maximum allowed driver price (validation ceiling). Used for schema and input max. */
export function getMaxAllowedDriverPrice(clientPrice: number): number {
  return floorCurrency(clientPrice * DRIVER_PRICE_MAX_PERCENT_THRESHOLD);
}

/** Default min/max driver bid bounds when client price changes. Uses default percentages, not thresholds. */
export function getDriverPriceBounds(clientPrice: number): {
  min: number;
  max: number;
} {
  return {
    min: roundCurrency(clientPrice * DRIVER_PRICE_DEFAULT_MIN_PERCENT),
    max: floorCurrency(clientPrice * DRIVER_PRICE_DEFAULT_MAX_PERCENT),
  };
}

export function formatEuro(value: number) {
  const locale = i18next.language === "fr" ? "fr-FR" : "en-US";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

export type DeliveryClientPriceSource = {
  clientFinalPrice?: number | null;
  clientInitialPrice?: number | null;
};

export type DeliveryDriverPriceSource = DeliveryClientPriceSource & {
  minPrice?: number | null;
  maxPrice?: number | null;
  medianPrice?: number | null;
  wantedPrice?: number | null;
};

export function getClientPrice(delivery: DeliveryClientPriceSource): number {
  return delivery.clientFinalPrice ?? delivery.clientInitialPrice ?? 0;
}

export function getInitialDriverPrices(delivery: DeliveryDriverPriceSource, clientPrice: number) {
  const min = delivery.minPrice ?? roundCurrency(clientPrice * DRIVER_PRICE_DEFAULT_MIN_PERCENT);
  const max = delivery.maxPrice ?? roundCurrency(clientPrice * DRIVER_PRICE_DEFAULT_MAX_PERCENT);
  const median = delivery.medianPrice ?? roundCurrency((min + max) / 2);
  const wanted =
    delivery.wantedPrice ?? roundCurrency(clientPrice * DRIVER_RECOMMENDED_PRICE_PERCENT);

  return { min, max, median, wanted };
}

export function getDriverPricesFromClientPrice(clientPrice: number) {
  const min = roundCurrency(clientPrice * DRIVER_PRICE_DEFAULT_MIN_PERCENT);
  const max = roundCurrency(clientPrice * DRIVER_PRICE_DEFAULT_MAX_PERCENT);
  const median = roundCurrency(
    clientPrice * ((DRIVER_PRICE_DEFAULT_MIN_PERCENT + DRIVER_PRICE_DEFAULT_MAX_PERCENT) / 2)
  );
  const wanted = roundCurrency(clientPrice * DRIVER_RECOMMENDED_PRICE_PERCENT);

  return { min, max, median, wanted };
}
